import React, { useEffect, useRef, useState } from 'react';

/**
 * One tile of a media grid — the Content Media Gallery today, the Virtual
 * Media Library's brand-wide grid later. Presentational and free of card
 * logic: a thumbnail (video-aware, loaded only once in view), an order
 * number, a selected ring, an optional hero mark, and a delete on hover
 * whose meaning the caller supplies.
 */
/** How the calling card frames its media — the tile shows what selecting
 *  would look like. `fit` letterboxes on a blurred copy (anchor + width);
 *  `fill` covers at the crop position. Without it: a square, covered. */
export interface TileFit {
  mode: 'fit' | 'fill';
  anchor?: 'top' | 'center' | 'bottom';
  /** Fit mode: the foreground's width in % of the tile. */
  width?: number;
  /** Fill mode: the crop position in %. */
  positionX?: number;
  positionY?: number;
}

export interface MediaTileProps {
  url: string;
  /** CSS aspect-ratio of the calling card's media (e.g. '9/16'); square when absent. */
  aspect?: string;
  fit?: TileFit;
  /** 1-based order number shown on the tile. */
  number: number;
  selected?: boolean;
  hero?: boolean;
  resolveUrl?: (url: string) => string | undefined;
  onSelect: () => void;
  onDelete?: () => void;
  deleteTitle?: string;
  /** Dragging: the sortable wrapper passes its props/handle here. */
  dragging?: boolean;
  className?: string;
  style?: React.CSSProperties;
  tileRef?: (el: HTMLElement | null) => void;
  dragProps?: Record<string, unknown>;
}

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const HOSTED_RE = /\/(tend-media|content-media)\//;

/** Our S3 videos have a poster next to them ('{key}.poster.jpg'). */
const posterFor = (url: string): string | undefined => (HOSTED_RE.test(url) ? `${url.split('?')[0]}.poster.jpg` : undefined);

const useInView = (ref: React.RefObject<HTMLElement>): boolean => {
  const [inView, setInView] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setInView(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
};

export const MediaTile: React.FC<MediaTileProps> = ({
  url, aspect, fit, number, selected = false, hero = false, resolveUrl, onSelect, onDelete, deleteTitle = 'Delete',
  dragging = false, className = '', style, tileRef, dragProps,
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(innerRef);
  const src = resolveUrl ? resolveUrl(url) : url;
  const isVideo = VIDEO_URL_RE.test(url);
  const poster = isVideo ? posterFor(url) : undefined;

  // The media as the calling card would frame it.
  const isFit = fit?.mode === 'fit';
  const fillPosition = fit?.mode === 'fill' ? `${fit.positionX ?? 50}% ${fit.positionY ?? 50}%` : 'center';
  const fitStyle: React.CSSProperties = isFit
    ? { width: `${fit?.width ?? 100}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${fit?.anchor ?? 'center'}`, position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }
    : { width: '100%', height: '100%', objectFit: 'cover', objectPosition: fillPosition };
  const media = (muted: boolean, cls: string, st: React.CSSProperties) => (
    isVideo ? (
      <video src={src} poster={poster} className={cls} style={st} muted={muted} playsInline preload="metadata" />
    ) : (
      <img src={src} alt="" className={cls} style={st} loading="lazy" draggable={false} />
    )
  );

  return (
    <div
      ref={(el) => { (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = el; tileRef?.(el); }}
      style={{ ...(aspect ? { aspectRatio: aspect } : {}), ...style }}
      className={`group/tile relative ${aspect ? '' : 'aspect-square'} rounded-lg overflow-hidden bg-black/40 border transition-shadow select-none
        ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-white/15 hover:border-white/40'}
        ${dragging ? 'opacity-60 shadow-2xl' : ''} ${className}`}
      data-testid="media-tile"
      data-url={url}
      data-selected={selected ? 'true' : 'false'}
      data-fit={fit?.mode ?? 'cover'}
      {...(dragProps || {})}
    >
      {/* The media — a button so Enter selects; loaded once in view */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        className="absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        title={isVideo ? 'Video' : 'Image'}
        aria-label={`Media ${number}${selected ? ', selected' : ''}`}
        aria-pressed={selected}
        data-testid="media-tile-select"
      >
        {inView && (
          <>
            {/* Fit mode: the blurred copy behind, like the strip */}
            {isFit && media(true, 'absolute inset-0 pointer-events-none', { width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(12px)', transform: 'scale(1.1)', opacity: 0.8 })}
            {media(true, `pointer-events-none ${isFit ? 'z-[1]' : ''}`, fitStyle)}
          </>
        )}
      </button>

      {/* Order number */}
      <span className="absolute top-1 left-1 min-w-[20px] h-5 px-1.5 rounded-full bg-black/70 text-white text-[11px] font-medium tabular-nums flex items-center justify-center pointer-events-none" data-testid="media-tile-number">
        {number}
      </span>

      {/* Hero mark (original card) */}
      {hero && (
        <span className="absolute bottom-1 left-1 px-1.5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center pointer-events-none" title="Hero image">
          Hero
        </span>
      )}

      {/* Video mark */}
      {isVideo && (
        <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center pointer-events-none">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
        </span>
      )}

      {/* Delete — on hover; the caller decides what it means */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover/tile:opacity-100 focus:opacity-100 hover:bg-red-600 transition-opacity"
          title={deleteTitle}
          aria-label={deleteTitle}
          data-testid="media-tile-delete"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default MediaTile;
